import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Accessibility as AccessibilityIcon, X, RotateCcw, Plus, Minus, Contrast, MousePointer2, Type, Link as LinkIcon, Heading, Pause, Droplet, Eye, EyeOff } from "lucide-react";

const STORAGE_KEY = "rts_a11y_settings_v1";

const defaultSettings = {
  textSize: 0, // -1..3
  spacing: false,
  readable: false,
  contrast: false,
  saturation: "normal", // normal | low | high | gray
  highlightLinks: false,
  highlightHeadings: false,
  pauseAnim: false,
  bigCursor: false,
};

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      return raw ? { ...defaultSettings, ...JSON.parse(raw) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });

  // apply to <html>
  useEffect(() => {
    const html = document.documentElement;
    const allClasses = [
      "a11y-text-110", "a11y-text-125", "a11y-text-150",
      "a11y-spacing", "a11y-readable", "a11y-contrast",
      "a11y-saturation-low", "a11y-saturation-high", "a11y-grayscale",
      "a11y-highlight-links", "a11y-highlight-headings",
      "a11y-pause-anim", "a11y-big-cursor",
    ];
    allClasses.forEach((c) => html.classList.remove(c));

    if (s.textSize === 1) html.classList.add("a11y-text-110");
    if (s.textSize === 2) html.classList.add("a11y-text-125");
    if (s.textSize === 3) html.classList.add("a11y-text-150");
    if (s.spacing) html.classList.add("a11y-spacing");
    if (s.readable) html.classList.add("a11y-readable");
    if (s.contrast) html.classList.add("a11y-contrast");
    if (s.saturation === "low") html.classList.add("a11y-saturation-low");
    if (s.saturation === "high") html.classList.add("a11y-saturation-high");
    if (s.saturation === "gray") html.classList.add("a11y-grayscale");
    if (s.highlightLinks) html.classList.add("a11y-highlight-links");
    if (s.highlightHeadings) html.classList.add("a11y-highlight-headings");
    if (s.pauseAnim) html.classList.add("a11y-pause-anim");
    if (s.bigCursor) html.classList.add("a11y-big-cursor");

    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
  }, [s]);

  const reset = () => setS(defaultSettings);

  const Tile = ({ active, onClick, icon: Icon, label, testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all text-xs font-semibold min-h-[88px] ${
        active
          ? "bg-[var(--rts-blue)] text-white border-[var(--rts-blue)] shadow-md"
          : "bg-white text-[var(--rts-ink)] border-gray-200 hover:border-[var(--rts-blue)] hover:bg-[var(--rts-blue-pale)]"
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-center leading-tight">{label}</span>
    </button>
  );

  return (
    <>
      {/* Floating button - bottom LEFT */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open accessibility menu"
        data-testid="a11y-toggle-button"
        className="fixed bottom-5 left-5 z-[60] w-14 h-14 rounded-full bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white shadow-2xl grid place-items-center transition-all hover:scale-105 focus:ring-4 focus:ring-[var(--rts-blue-light)]"
      >
        <AccessibilityIcon className="w-7 h-7" />
        <span className="sr-only">Accessibility options</span>
      </button>

      {/* Modal/Drawer */}
      {open && (
        <div className="fixed inset-0 z-[70] flex" data-testid="a11y-panel" role="dialog" aria-label="Accessibility options">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <aside className="relative ml-0 mr-auto h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-fade-up">
            <div className="sticky top-0 bg-[var(--rts-blue-deep)] text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AccessibilityIcon className="w-6 h-6" />
                <h2 className="font-display text-lg font-bold">Accessibility</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="a11y-close-button"
                className="p-1.5 rounded-md hover:bg-white/15"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Text size */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--rts-blue-deep)]">Text Size</h3>
                  <span className="text-xs text-[var(--rts-muted)]">{["Default","+10%","+25%","+50%"][s.textSize] || "Default"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setS({ ...s, textSize: Math.max(0, s.textSize - 1) })}
                    data-testid="a11y-text-decrease"
                    className="flex-1 py-2 rounded-lg border-2 border-gray-200 hover:border-[var(--rts-blue)] flex items-center justify-center gap-1 font-semibold text-sm"
                  >
                    <Minus className="w-4 h-4" /> Smaller
                  </button>
                  <button
                    onClick={() => setS({ ...s, textSize: Math.min(3, s.textSize + 1) })}
                    data-testid="a11y-text-increase"
                    className="flex-1 py-2 rounded-lg border-2 border-gray-200 hover:border-[var(--rts-blue)] flex items-center justify-center gap-1 font-semibold text-sm"
                  >
                    <Plus className="w-4 h-4" /> Larger
                  </button>
                </div>
              </section>

              {/* Reading aids */}
              <section>
                <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--rts-blue-deep)] mb-3">Reading</h3>
                <div className="grid grid-cols-3 gap-2">
                  <Tile active={s.spacing} onClick={() => setS({ ...s, spacing: !s.spacing })} icon={Type} label="Letter Spacing" testid="a11y-spacing" />
                  <Tile active={s.readable} onClick={() => setS({ ...s, readable: !s.readable })} icon={Type} label="Readable Font" testid="a11y-readable" />
                  <Tile active={s.highlightLinks} onClick={() => setS({ ...s, highlightLinks: !s.highlightLinks })} icon={LinkIcon} label="Highlight Links" testid="a11y-highlight-links" />
                  <Tile active={s.highlightHeadings} onClick={() => setS({ ...s, highlightHeadings: !s.highlightHeadings })} icon={Heading} label="Highlight Headings" testid="a11y-highlight-headings" />
                  <Tile active={s.pauseAnim} onClick={() => setS({ ...s, pauseAnim: !s.pauseAnim })} icon={Pause} label="Pause Animations" testid="a11y-pause-anim" />
                  <Tile active={s.bigCursor} onClick={() => setS({ ...s, bigCursor: !s.bigCursor })} icon={MousePointer2} label="Big Cursor" testid="a11y-big-cursor" />
                </div>
              </section>

              {/* Color */}
              <section>
                <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--rts-blue-deep)] mb-3">Color & Contrast</h3>
                <div className="grid grid-cols-3 gap-2">
                  <Tile active={s.contrast} onClick={() => setS({ ...s, contrast: !s.contrast })} icon={Contrast} label="High Contrast" testid="a11y-contrast" />
                  <Tile active={s.saturation === "low"} onClick={() => setS({ ...s, saturation: s.saturation === "low" ? "normal" : "low" })} icon={EyeOff} label="Low Saturation" testid="a11y-saturation-low" />
                  <Tile active={s.saturation === "high"} onClick={() => setS({ ...s, saturation: s.saturation === "high" ? "normal" : "high" })} icon={Eye} label="High Saturation" testid="a11y-saturation-high" />
                  <Tile active={s.saturation === "gray"} onClick={() => setS({ ...s, saturation: s.saturation === "gray" ? "normal" : "gray" })} icon={Droplet} label="Grayscale" testid="a11y-grayscale" />
                </div>
              </section>

              {/* Reset + statement link */}
              <section className="border-t pt-5 space-y-3">
                <button
                  onClick={reset}
                  data-testid="a11y-reset"
                  className="w-full py-3 rounded-xl bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] text-white font-bold flex items-center justify-center gap-2 transition"
                >
                  <RotateCcw className="w-4 h-4" /> Reset All Settings
                </button>
                <Link
                  to="/accessibility"
                  onClick={() => setOpen(false)}
                  data-testid="a11y-statement-link"
                  className="block text-center text-sm font-semibold text-[var(--rts-blue-deep)] hover:underline"
                >
                  Read full Accessibility Statement →
                </Link>
                <p className="text-xs text-[var(--rts-muted)] text-center leading-relaxed">
                  We are committed to WCAG 2.1 Level AA. Need help? <a href="mailto:ReversingStigma@gmail.com" className="underline text-[var(--rts-blue-deep)]">Contact us</a>.
                </p>
              </section>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
